import { RestockItem, RestockLocation } from '@/domain/restock'
import { clsx } from 'clsx'
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  MapIcon,
  ChevronIcon,
  CheckIcon,
  Checkbox
} from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

interface RestockRowProps {
  isRestockOnlyMode: boolean
  restock: RestockItem
  restockLocations: RestockLocation[]
  onChangeRestock: (shouldRestock: boolean) => void
  onChangeLocation: (location?: RestockLocation) => void
}

export const RestockRow: React.FC<RestockRowProps> = ({
  isRestockOnlyMode,
  restock,
  restockLocations,
  onChangeRestock,
  onChangeLocation
}) => {
  const { t } = useTranslation()

  const [selectedLocation, setSelectedLocation] = useState(
    restockLocations.find((l) => l.isDefault) ?? restockLocations[0]
  )

  const isSelectedLocation = (id: string) => {
    return restock.location?.id === id
  }

  const hasLocations = restockLocations.length > 0

  return (
    <div>
      <hr className="my-2 border-[#E1E4EAFF]" />
      <div
        className={clsx(
          'flex h-8 items-center justify-end gap-2',
          isRestockOnlyMode ? 'justify-between' : ''
        )}
      >
        <div className="flex items-center gap-1">
          {isRestockOnlyMode ? null : (
            <Checkbox
              onChange={(checked) => {
                onChangeRestock(checked)

                if (selectedLocation) {
                  if (checked) {
                    onChangeLocation(selectedLocation)
                  } else {
                    onChangeLocation(undefined)
                  }
                }
              }}
              checked={restock.status === 'SHOULD_RESTOCK'}
              className="cursor-pointer"
            />
          )}

          {isRestockOnlyMode ? (
            <div className="flex flex-col">
              <p className="text-sm">{t('products_tab.location')}</p>
              <p className="text-xs text-[#99A0AEFF]">
                {t('products_tab.preferred_location')}
              </p>
            </div>
          ) : (
            <p className="text-sm text-neutral-500">
              {t(
                hasLocations
                  ? 'products_tab.restock_item_at'
                  : 'products_tab.restock_item'
              )}
            </p>
          )}
        </div>
        {hasLocations ? (
          <Menu>
            <MenuButton
              className={clsx(
                'rounded-lg border border-solid border-[#E1E4EAFF] p-1',
                'flex items-center gap-2',
                'hover:bg-neutral-50 hover:shadow-sm',
                'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:hover:bg-neutral-100 disabled:hover:shadow-none'
              )}
              styled={false}
              onClick={(e) => e.stopPropagation()}
              disabled={restock.status !== 'SHOULD_RESTOCK'}
            >
              {isRestockOnlyMode ? (
                <MapIcon className="text-[#99A0AEFF]" />
              ) : null}
              <p className="text-sm text-neutral-800">
                {selectedLocation.name ?? '-'}
              </p>
              <ChevronIcon className={clsx('size-5 text-[#99A0AEFF]')} />
            </MenuButton>
            <MenuItems
              anchor={{
                to: 'bottom start'
              }}
              className="my-1 w-fit min-w-[var(--button-width)] rounded-lg border border-solid border-[#E1E4EAFF] bg-white p-2 shadow-lg"
              styled={false}
              onClick={(e) => e.stopPropagation()}
            >
              {restockLocations.map((r) => (
                <MenuItem key={r.id}>
                  <p
                    className={clsx(
                      'group relative text-sm font-light text-[#0A0D14FF] hover:bg-[#F6F8FAFF]',
                      'flex cursor-pointer items-center justify-between gap-2 rounded-md p-2 pr-10'
                    )}
                    onClick={() => {
                      onChangeLocation(r)
                      setSelectedLocation(r)
                    }}
                  >
                    {r.name}
                    <CheckIcon
                      className={clsx(
                        'absolute right-2',
                        isSelectedLocation(r.id) ? 'visible' : 'invisible'
                      )}
                    />
                  </p>
                </MenuItem>
              ))}
            </MenuItems>
          </Menu>
        ) : null}
      </div>
    </div>
  )
}
